/**
 * BlauWeiss "Mein BMW": gewählte Generation merken und überall nutzen.
 * - <bw-fahrzeug-waehler>   sections/bw-fahrzeug-waehler.liquid (Modell aufklappen, Generation merken)
 * - <bw-mein-bmw-chip>      sections/bw-mein-bmw.liquid (Chip im Header-Bereich)
 * - <bw-passform-hinweis>   blocks/bw-passform-hinweis.liquid (Produktseite)
 * - <bw-mein-bmw-filter>    blocks/bw-mein-bmw-filter.liquid (Kollektionsseite)
 * Gespeichert in localStorage (try/catch – ohne Speicher funktioniert die Seite normal weiter).
 */
const STORAGE_KEY = 'bw-mein-bmw';
const CHANGE_EVENT = 'bw:mein-bmw';
const CODE_PATTERN = /^[A-Z]\d{2}$/;

/** @typedef {{ model: string, code: string, label: string, tags: string[], url: string, modelUrl: string }} MeinBmw */

/** @returns {MeinBmw | null} */
function readCar() {
  try {
    const car = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    // Alte Auswahl mit Tag-Filter-Link (vor den Generations-Kollektionen) verwerfen
    if (car && typeof car.url === 'string' && car.url.includes('filter.')) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return car && Array.isArray(car.tags) && car.label ? car : null;
  } catch {
    return null;
  }
}

/** @param {MeinBmw | null} car */
function writeCar(car) {
  try {
    if (car) localStorage.setItem(STORAGE_KEY, JSON.stringify(car));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Speicher blockiert (z. B. privates Fenster) – Auswahl gilt dann nur für diese Seite
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: car }));
}

/**
 * @param {string | undefined} template
 * @param {MeinBmw} car
 */
function fill(template, car) {
  return (template || '').replaceAll('[bmw]', car.label).replaceAll('[code]', car.code);
}

/** @param {string | undefined} json */
function parseList(json) {
  try {
    const list = JSON.parse(json || '[]');
    return Array.isArray(list) ? list.map((tag) => String(tag).trim().toUpperCase()) : [];
  } catch {
    return [];
  }
}

/** Shop-interne Pfade, damit gespeicherte Werte nie auf fremde Seiten führen */
function safeUrl(url) {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : null;
}

/** Basis für Elemente, die auf "Mein BMW" reagieren */
class MeinBmwElement extends HTMLElement {
  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.update);
    window.addEventListener('storage', this.update);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.update);
    window.removeEventListener('storage', this.update);
  }

  update = () => {
    this.render(readCar());
  };

  /** @param {MeinBmw | null} _car */
  render(_car) {}
}

class BwFahrzeugWaehler extends HTMLElement {
  connectedCallback() {
    this.tiles = [...this.querySelectorAll('[data-bw-modell-tile]')];
    this.addEventListener('click', this.#handleClick);

    const car = readCar();
    if (car) {
      this.querySelectorAll('[data-bw-generation]').forEach((link) => {
        const active = link.closest('[data-bw-modell-panel]')?.dataset.modelLabel === car.model && link.dataset.code === car.code;
        if (active) link.setAttribute('aria-current', 'true');
      });
      // "ändern" im Chip springt hierher – dann das gemerkte Modell gleich aufklappen
      if (this.id && window.location.hash === `#${this.id}`) {
        const tile = this.tiles.find((tile) => tile.dataset.modelLabel === car.model);
        if (tile) this.#open(tile);
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tile = event.target.closest('[data-bw-modell-tile]');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      const panel = this.#panelFor(tile);
      if (!panel) return;
      event.preventDefault();
      if (tile.getAttribute('aria-expanded') === 'true') this.#close(tile);
      else this.#open(tile, true);
      return;
    }

    const generation = event.target.closest('[data-bw-generation]');
    if (generation instanceof HTMLAnchorElement) {
      const panel = generation.closest('[data-bw-modell-panel]');
      const model = panel?.dataset.modelLabel || '';
      const title = generation.dataset.code || '';
      writeCar({
        model,
        code: title,
        label: `${model} ${title}`.trim(),
        tags: parseList(generation.dataset.tags),
        url: generation.getAttribute('href') || '',
        modelUrl: panel?.dataset.modelUrl || '',
      });
      // Link führt danach ganz normal zur gefilterten Kollektion
    }
  };

  /** @param {HTMLElement} tile */
  #panelFor(tile) {
    const id = tile.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /**
   * @param {HTMLElement} tile
   * @param {boolean} [scroll]
   */
  #open(tile, scroll = false) {
    this.tiles.forEach((other) => other !== tile && this.#close(other));
    const panel = this.#panelFor(tile);
    if (!panel) return;
    tile.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    if (scroll) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    }
  }

  /** @param {HTMLElement} tile */
  #close(tile) {
    tile.setAttribute('aria-expanded', 'false');
    const panel = this.#panelFor(tile);
    if (panel) panel.hidden = true;
  }
}

class BwMeinBmwChip extends MeinBmwElement {
  connectedCallback() {
    super.connectedCallback();
    this.querySelector('[data-bw-remove]')?.addEventListener('click', this.#remove);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelector('[data-bw-remove]')?.removeEventListener('click', this.#remove);
  }

  #remove = () => writeCar(null);

  /** @param {MeinBmw | null} car */
  render(car) {
    this.hidden = !car;
    if (!car) return;
    const label = this.querySelector('[data-bw-label]');
    if (label) {
      label.textContent = fill(this.dataset.template, car);
      const url = safeUrl(car.url);
      if (url && label instanceof HTMLAnchorElement) label.href = url;
    }
  }
}

class BwPassformHinweis extends MeinBmwElement {
  /** @param {MeinBmw | null} car */
  render(car) {
    const productTags = parseList(this.dataset.tags);
    const universal = this.dataset.universal === 'true';
    const hasCodes = productTags.some((tag) => CODE_PATTERN.test(tag));
    const fits = car ? productTags.some((tag) => car.tags.includes(tag)) : false;

    // Ohne Auswahl oder ohne Passform-Angaben am Produkt: nichts behaupten
    let state = '';
    if (car && universal) state = 'universal';
    else if (car && fits) state = 'fits';
    else if (car && hasCodes) state = 'no-fit';

    this.hidden = !state;
    this.dataset.state = state;
    if (!car || !state) return;

    const text = this.querySelector('[data-bw-text]');
    if (text) text.textContent = fill(this.dataset[`text${state === 'no-fit' ? 'NoFit' : state === 'fits' ? 'Fits' : 'Universal'}`], car);

    const link = this.querySelector('[data-bw-link]');
    if (link instanceof HTMLAnchorElement) {
      const url = safeUrl(car.url);
      link.hidden = state !== 'no-fit' || !url;
      if (url) link.href = url;
    }
  }
}

class BwMeinBmwFilter extends MeinBmwElement {
  /** @param {MeinBmw | null} car */
  render(car) {
    const path = (url) => (url || '').split('?')[0];
    const generationUrl = car ? safeUrl(path(car.url)) : null;
    const modelUrl = car ? safeUrl(path(car.modelUrl)) : null;
    const current = path(this.dataset.collectionUrl) || window.location.pathname;
    const onGeneration = Boolean(generationUrl) && current === generationUrl;

    // Sichtbar auf der Generations-Kollektion selbst oder in Kollektionen mit Teilen für diese Generation
    const collectionTags = parseList(this.dataset.tags);
    const hasParts = car ? car.tags.some((tag) => collectionTags.includes(tag)) : false;
    const target = onGeneration ? modelUrl : generationUrl;
    this.hidden = !car || !target || !(onGeneration || hasParts);
    if (this.hidden || !car || !target) return;

    const text = this.querySelector('[data-bw-text]');
    const link = this.querySelector('[data-bw-link]');
    if (text) text.textContent = fill(onGeneration ? this.dataset.textActive : this.dataset.text, car);
    if (link instanceof HTMLAnchorElement) {
      link.textContent = fill(onGeneration ? this.dataset.linkActive : this.dataset.link, car);
      link.href = target;
    }
  }
}

const elements = {
  'bw-fahrzeug-waehler': BwFahrzeugWaehler,
  'bw-mein-bmw-chip': BwMeinBmwChip,
  'bw-passform-hinweis': BwPassformHinweis,
  'bw-mein-bmw-filter': BwMeinBmwFilter,
};

for (const [name, element] of Object.entries(elements)) {
  if (!customElements.get(name)) customElements.define(name, element);
}
